/**
 * Career Stats — Materialized career statistics for athlete profiles.
 * Updated after workouts, duels, tournaments, and season completions.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';
import { calculateDOTS, lbsToKg } from './dotsCoefficient';

const log = createLogger('CAREER_STATS');

export interface CareerStats {
  total_competitions: number;
  total_workouts: number;
  tournament_wins: number;
  season_championships: number;
  duel_wins: number;
  duel_losses: number;
  duel_win_streak: number;
  best_workout_streak: number;
  best_total_kg: number | null;
  best_squat_kg: number | null;
  best_bench_kg: number | null;
  best_deadlift_kg: number | null;
  best_dots: number | null;
  followers_count: number;
  following_count: number;
}

/** Recompute career stats for a profile (called after significant events) */
export async function recomputeCareerStats(profileId: string): Promise<void> {
  try {
    // Workout count
    const { count: totalWorkouts } = await supabase
      .from('workouts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', profileId);

    // Duel record
    const { count: duelWins } = await supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .eq('winner_user_id', profileId)
      .in('status', ['completed', 'resolved']);

    const { count: duelLosses } = await supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .neq('winner_user_id', profileId)
      .or(`challenger_id.eq.${profileId},challenged_id.eq.${profileId}`)
      .in('status', ['completed', 'resolved']);

    // Best lifts from strength_events
    const liftBests: Record<string, number | null> = {
      'back-squat': null,
      'bench-press': null,
      'deadlift': null,
    };

    for (const liftKey of Object.keys(liftBests)) {
      const { data } = await supabase
        .from('strength_events')
        .select('weight, weight_unit')
        .eq('profile_id', profileId)
        .eq('lift_key', liftKey)
        .order('weight', { ascending: false })
        .limit(1);

      if (data?.[0]) {
        const w = data[0].weight;
        liftBests[liftKey] = data[0].weight_unit === 'lb' ? w * 0.45359237 : w;
      }
    }

    const bestTotal = (liftBests['back-squat'] || 0) + (liftBests['bench-press'] || 0) + (liftBests['deadlift'] || 0);

    // Followers
    const { count: followers } = await supabase
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('following_id', profileId);

    const { count: following } = await supabase
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('follower_id', profileId);

    // Tournament wins
    const { count: tournamentWins } = await supabase
      .from('tournament_participants')
      .select('id', { count: 'exact', head: true })
      .eq('profile_id', profileId)
      .eq('status', 'winner');

    // Season championships (final_rank = 1)
    const { count: seasonChampionships } = await supabase
      .from('season_participants')
      .select('id', { count: 'exact', head: true })
      .eq('profile_id', profileId)
      .eq('final_rank', 1);

    // Total competitions: duels + tournament participations + season participations
    const { count: totalDuels } = await supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .or(`challenger_id.eq.${profileId},challenged_id.eq.${profileId}`)
      .in('status', ['completed', 'resolved']);

    const { count: seasonParticipations } = await supabase
      .from('season_participants')
      .select('id', { count: 'exact', head: true })
      .eq('profile_id', profileId);

    const totalCompetitions = (totalDuels ?? 0) + (tournamentWins ?? 0) + (seasonParticipations ?? 0);

    // Best DOTS: real IPF polynomial via calculateDOTS
    const { data: profileData } = await supabase
      .from('profiles')
      .select('bodyweight, gender')
      .eq('id', profileId)
      .maybeSingle();
    const bwKg = profileData?.bodyweight ? (profileData.bodyweight * 0.45359237) : 0;
    const gender = (profileData as any)?.gender === 'female' ? 'female' : 'male';
    const totalKg = lbsToKg(bestTotal);
    const bestDots = totalKg > 0 && bwKg > 0 ? calculateDOTS(totalKg, bwKg, gender) : null;

    // Duel win streak — count consecutive wins from most recent duels
    const { data: recentDuels } = await supabase
      .from('duels')
      .select('winner_user_id, status')
      .or(`challenger_id.eq.${profileId},challenged_id.eq.${profileId}`)
      .in('status', ['completed', 'resolved'])
      .order('updated_at', { ascending: false })
      .limit(50);

    let duelWinStreak = 0;
    for (const d of recentDuels ?? []) {
      if (d.winner_user_id === profileId) duelWinStreak++;
      else break;
    }

    // Best workout streak — consecutive days with at least 1 workout
    const { data: workoutDates } = await supabase
      .from('workouts')
      .select('performed_at')
      .eq('user_id', profileId)
      .order('performed_at', { ascending: false })
      .limit(365);

    let bestWorkoutStreak = 0;
    let currentStreak = 0;
    let prevDate: string | null = null;
    for (const w of workoutDates ?? []) {
      const day = w.performed_at.slice(0, 10);
      if (!prevDate) { currentStreak = 1; }
      else {
        const diff = Math.round((new Date(prevDate).getTime() - new Date(day).getTime()) / 86400000);
        if (diff === 1) currentStreak++;
        else if (diff > 1) currentStreak = 1;
        // diff === 0 means same day — skip
      }
      if (currentStreak > bestWorkoutStreak) bestWorkoutStreak = currentStreak;
      if (day !== prevDate) prevDate = day;
    }

    // Upsert
    await supabase
      .from('career_stats')
      .upsert({
        profile_id: profileId,
        total_workouts: totalWorkouts ?? 0,
        duel_wins: duelWins ?? 0,
        duel_losses: duelLosses ?? 0,
        tournament_wins: tournamentWins ?? 0,
        season_championships: seasonChampionships ?? 0,
        total_competitions: totalCompetitions,
        best_dots: bestDots,
        duel_win_streak: duelWinStreak,
        best_workout_streak: bestWorkoutStreak,
        best_squat_kg: liftBests['back-squat'],
        best_bench_kg: liftBests['bench-press'],
        best_deadlift_kg: liftBests['deadlift'],
        best_total_kg: bestTotal > 0 ? bestTotal : null,
        followers_count: followers ?? 0,
        following_count: following ?? 0,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'profile_id' });

    log.info('Career stats updated', { profileId });
  } catch (err) {
    log.error('recomputeCareerStats failed:', err);
  }
}

/** Fetch career stats for display */
export async function fetchCareerStats(profileId: string): Promise<CareerStats | null> {
  const { data } = await supabase
    .from('career_stats')
    .select('*')
    .eq('profile_id', profileId)
    .maybeSingle();

  return data;
}

/** Record a PR to the timeline */
export async function recordPR(
  profileId: string,
  liftKey: string,
  weightKg: number,
  previousPrKg: number | null,
  videoUrl?: string,
  contextType?: string,
  contextId?: string,
): Promise<void> {
  const improvement = previousPrKg ? weightKg - previousPrKg : null;
  const improvementPct = previousPrKg && previousPrKg > 0
    ? ((weightKg - previousPrKg) / previousPrKg) * 100
    : null;

  await supabase
    .from('pr_timeline')
    .insert({
      profile_id: profileId,
      lift_key: liftKey,
      weight_kg: weightKg,
      previous_pr_kg: previousPrKg,
      improvement_kg: improvement,
      improvement_pct: improvementPct,
      context_type: contextType || 'training',
      context_id: contextId,
      video_url: videoUrl,
      performed_at: new Date().toISOString(),
    });
}
